import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { EnhancedDialog } from 'components/enhanced';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { locationService } from 'shared/services';
import toast from 'shared/utils/toast';
import { Option } from 'shared/types/general';
import { productWarehouseService } from '../../api/product-warehouse.service';

interface DefaultLocationFormParams {
  // eslint-disable-next-line
  row: any;
  open: boolean;
  onClose: () => void;
  // refresher functions
  refresh?: React.RefObject<() => void>;
}

function DefaultLocationDialogForm({ row, open, onClose, refresh }: DefaultLocationFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.edit.misc_product_warehouse_form';

  const [locations, setLocations] = useState<Option[]>([]);

  useEffect(() => {
    locationService.getAllBaseInformation({ warehouse: row.warehouse }).then(setLocations);
  }, [row.warehouse]);

  const defaultLocationInit = useMemo(
    () => locations.find((l) => l.code === row.default_location) || undefined,
    [locations, row.default_location]
  );

  // eslint-disable-next-line
  const handleSubmit = async (values: any) => {
    try {
      await productWarehouseService.setDefaultLocation(
        row.product,
        row.warehouse,
        values.default_location ?? ''
      );
      if (refresh && refresh.current) {
        refresh.current();
      }
      toast.success(t('actions.successfully.updated'));
      onClose();
    } catch {
      // Fall through; backend returns 400 with a descriptive error.
    }
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title={t(`${translationPrefix}.default-location-dialog-title`)}
      subTitle={t(`${translationPrefix}.default-location-dialog-subtitle`, {
        product: row.product,
        warehouse: row.warehouse
      })}
      submitLabel={t('actions.save')}
      cancelLabel={t('actions.cancel')}
      maxWidth="sm"
      submitButtonProps={{
        type: 'submit',
        form: 'configureDefaultLocationForm'
      }}>
      <EnhancedForm
        onSubmit={handleSubmit}
        hideControls
        initialValues={{ default_location: row.default_location || '' }}
        formId="configureDefaultLocationForm"
        autoStyleLayout
        columns={1}>
        {locations.length > 0 && (
          <IdentityField
            name="default_location"
            component={AutocompleteAdapter}
            options={locations}
            initValue={defaultLocationInit}
            label={t(`${translationPrefix}.default-location`)}
            type="text"
          />
        )}
      </EnhancedForm>
    </EnhancedDialog>
  );
}

export default DefaultLocationDialogForm;
